import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, AlertTriangle, BookOpenCheck, GitBranch, Gauge, Landmark } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { friendlyMessage } from '@/api/http'
import { branchApi } from '@/api/branchApi'
import { storyStateApi, type SetupPayoffItem, type StoryStateTrendPoint } from '@/api/storyStateApi'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import type { Chapter, Project } from '@/types/nf'
import { getBranchDisplayName } from './CharacterStatePanel'

function recallRate(point: StoryStateTrendPoint) {
  const total = point.foreshadow_resolved + point.foreshadow_overdue
  if (total <= 0) return 0
  return Math.round((point.foreshadow_resolved / total) * 100)
}

function scaledPolyline(points: StoryStateTrendPoint[], valueOf: (point: StoryStateTrendPoint) => number) {
  if (!points.length) return ''
  const values = points.map(valueOf)
  const max = Math.max(1, ...values)
  const width = 320
  const height = 120
  return points
    .map((point, index) => {
      const x = points.length === 1 ? width / 2 : (index / (points.length - 1)) * width
      const y = height - (valueOf(point) / max) * height
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

function TrendChart({ points }: { points: StoryStateTrendPoint[] }) {
  const recallLine = scaledPolyline(points, recallRate)
  const densityLine = scaledPolyline(points, (point) => point.subplot_density)
  if (!points.length) {
    return <p className="text-sm text-muted-foreground">-</p>
  }
  return (
    <svg viewBox="0 0 320 140" role="img" className="h-40 w-full overflow-visible">
      <line x1="0" y1="120" x2="320" y2="120" className="stroke-border" />
      <line x1="0" y1="60" x2="320" y2="60" className="stroke-border/70" strokeDasharray="4 4" />
      <polyline fill="none" stroke="currentColor" strokeWidth="3" points={recallLine} className="text-emerald-600" />
      <polyline fill="none" stroke="currentColor" strokeWidth="3" points={densityLine} className="text-sky-600" />
      {points.map((point, index) => {
        const x = points.length === 1 ? 160 : (index / (points.length - 1)) * 320
        return (
          <text key={point.chapter_no} x={x} y="138" textAnchor="middle" className="fill-muted-foreground text-[10px]">
            {point.chapter_no}
          </text>
        )
      })}
    </svg>
  )
}

function DebtList({
  title,
  items,
  empty,
}: {
  title: string
  items: SetupPayoffItem[]
  empty: string
}) {
  return (
    <Card className="rounded-lg shadow-xs">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length ? (
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.id} className="rounded-md border bg-background/40 p-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 break-words text-sm font-medium">{item.title}</p>
                  <Badge variant="destructive" className="shrink-0">
                    +{item.overdue_by_chapters}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">#{item.target_chapter}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{empty}</p>
        )}
      </CardContent>
    </Card>
  )
}

export function StoryStatePanel({ project, chapter }: { project: Project; chapter?: Chapter | null }) {
  const { t } = useTranslation()
  const [branchId, setBranchId] = useState('main')
  const [chapterText, setChapterText] = useState(String(chapter?.chapter_number ?? 1))
  const chapterNumber = Math.max(1, Number(chapterText) || chapter?.chapter_number || 1)
  const [trendFromText, setTrendFromText] = useState(String(Math.max(1, chapterNumber - 7)))
  const [trendToText, setTrendToText] = useState(String(chapterNumber))
  const trendFrom = Math.max(1, Number(trendFromText) || 1)
  const trendTo = Math.max(1, Number(trendToText) || chapterNumber)
  const trendRangeValid = trendFrom <= trendTo && trendTo - trendFrom <= 1000

  const branchesQuery = useQuery({
    queryKey: ['branches', project.id],
    queryFn: () => branchApi.getBranches(project.id),
  })
  const storyStateQuery = useQuery({
    queryKey: ['story-state', project.id, branchId, chapterNumber],
    queryFn: () => storyStateApi.get(project.id, { branchId, chapter: chapterNumber }),
  })
  const trendQuery = useQuery({
    queryKey: ['story-state-trend', project.id, branchId, trendFrom, trendTo],
    queryFn: () => storyStateApi.getTrend(project.id, { branchId, from: trendFrom, to: trendTo }),
    enabled: trendRangeValid,
  })

  const branchOptions = branchesQuery.data?.length ? branchesQuery.data : [{ id: 'main', name: 'main' }]
  const state = storyStateQuery.data
  const trendPoints = trendQuery.data?.points ?? []
  const trendRows = trendPoints.slice(-6)
  const wordBudgetPercent = useMemo(() => {
    if (!state?.word_budget.volume_chapter_count) return 0
    return Math.min(100, Math.round((state.word_budget.chapter_index_in_volume / state.word_budget.volume_chapter_count) * 100))
  }, [state])

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-normal">
            <Gauge className="size-5 text-primary" />
            {t('storyState.title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('storyState.subtitle')}</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-[220px_120px]">
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger>
              <GitBranch className="size-4 text-muted-foreground" />
              <SelectValue placeholder={t('characterState.branchPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {branchOptions.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {getBranchDisplayName(branch, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="space-y-1">
            <Label htmlFor="story-state-chapter" className="sr-only">
              {t('storyState.chapter')}
            </Label>
            <Input
              id="story-state-chapter"
              type="number"
              min="1"
              value={chapterText}
              onChange={(event) => setChapterText(event.target.value)}
            />
          </div>
        </div>
      </div>

      {storyStateQuery.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Skeleton className="h-44 rounded-lg" />
          <Skeleton className="h-44 rounded-lg" />
          <Skeleton className="h-44 rounded-lg" />
          <Skeleton className="h-44 rounded-lg" />
        </div>
      ) : storyStateQuery.error ? (
        <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
          {friendlyMessage(storyStateQuery.error, t('storyState.loadFailed'))}
        </div>
      ) : state ? (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <Card className="rounded-lg shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="size-4 text-destructive" />
                  {t('storyState.debtTotal')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-3xl font-semibold">
                {state.overdue_foreshadows.length + state.overdue_subplots.length}
              </CardContent>
            </Card>
            <Card className="rounded-lg shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Landmark className="size-4 text-primary" />
                  {t('storyState.convergenceDensity')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-3xl font-semibold">{state.convergence_density}</CardContent>
            </Card>
            <Card className="rounded-lg shadow-xs">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BookOpenCheck className="size-4 text-primary" />
                  {t('storyState.currentPosition')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm">
                  {t('storyState.positionValue', {
                    chapter: state.current_chapter_number,
                    volume: state.current_volume_number || '-',
                  })}
                </p>
                <Progress value={wordBudgetPercent} />
                <p className="text-xs text-muted-foreground">
                  {t('storyState.wordBudgetValue', {
                    current: state.word_budget.chapter_index_in_volume || 0,
                    total: state.word_budget.volume_chapter_count || 0,
                    words: state.word_budget.volume_budget_words || 0,
                  })}
                </p>
              </CardContent>
            </Card>
          </div>

          {state.context_digest_summary ? (
            <Card className="rounded-lg shadow-xs">
              <CardHeader>
                <CardTitle className="text-base">{t('storyState.contextDigest')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground">
                  {state.context_digest_summary}
                </p>
              </CardContent>
            </Card>
          ) : null}

          <div className="grid gap-3 md:grid-cols-2">
            <DebtList
              title={t('storyState.overdueForeshadows')}
              items={state.overdue_foreshadows}
              empty={t('storyState.noForeshadows')}
            />
            <DebtList
              title={t('storyState.overdueSubplots')}
              items={state.overdue_subplots}
              empty={t('storyState.noSubplots')}
            />
          </div>

          <Card className="rounded-lg shadow-xs">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="size-4 text-primary" />
                  {t('storyState.trendTitle')}
                </CardTitle>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    type="number"
                    min="1"
                    value={trendFromText}
                    aria-label={t('storyState.trendFrom')}
                    onChange={(event) => setTrendFromText(event.target.value)}
                  />
                  <Input
                    type="number"
                    min="1"
                    value={trendToText}
                    aria-label={t('storyState.trendTo')}
                    onChange={(event) => setTrendToText(event.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!trendRangeValid ? (
                <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  {t('storyState.trendInvalidRange')}
                </p>
              ) : trendQuery.isLoading ? (
                <Skeleton className="h-44 rounded-lg" />
              ) : trendQuery.error ? (
                <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  {friendlyMessage(trendQuery.error, t('storyState.trendLoadFailed'))}
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-5 bg-emerald-600" />
                      {t('storyState.recallRate')}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-5 bg-sky-600" />
                      {t('storyState.subplotDensity')}
                    </span>
                  </div>
                  <TrendChart points={trendPoints} />
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] text-left text-sm">
                      <thead className="text-xs text-muted-foreground">
                        <tr>
                          <th className="py-2 pr-3 font-medium">{t('storyState.chapter')}</th>
                          <th className="py-2 pr-3 font-medium">{t('storyState.recallRate')}</th>
                          <th className="py-2 pr-3 font-medium">{t('storyState.resolved')}</th>
                          <th className="py-2 pr-3 font-medium">{t('storyState.overdue')}</th>
                          <th className="py-2 font-medium">{t('storyState.subplotDensity')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trendRows.map((point) => (
                          <tr key={point.chapter_no} className="border-t">
                            <td className="py-2 pr-3">#{point.chapter_no}</td>
                            <td className="py-2 pr-3">{recallRate(point)}%</td>
                            <td className="py-2 pr-3">{point.foreshadow_resolved}</td>
                            <td className="py-2 pr-3">{point.foreshadow_overdue}</td>
                            <td className="py-2">{point.subplot_density}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  )
}
